import { finalizeLogin } from '@/features/authentication/api/accountAccess';
import { useLoginFinalizeAttemptStore } from '@/features/authentication/stores/loginFinalizeAttemptStore';
import type { LoginAttempt } from '@/features/authentication/types/accountAccess';
import {
  type AuthTokenResult,
  getAuthTokenResult,
  getCurrentAuthUser,
} from '@/shared/lib/authSession';
import type { AuthUser } from '@/shared/types/auth';

function assertCurrentUser(user: AuthUser): void {
  if (getCurrentAuthUser()?.uid !== user.uid) throw new Error('login-session-changed');
}

export function clearLoginAttempt(): void {
  useLoginFinalizeAttemptStore.getState().clear();
}

async function finalizeAndVerify(
  user: AuthUser,
  token: AuthTokenResult,
  attempt: LoginAttempt,
): Promise<void> {
  assertCurrentUser(user);
  const result = await finalizeLogin(token.token, attempt.invitationToken ?? undefined, {
    idempotencyKey: attempt.key,
  });
  assertCurrentUser(user);
  let currentToken = token;
  if (result.tokenRefreshRequired) {
    const refreshed = await getAuthTokenResult(true);
    if (!refreshed) throw new Error('login-session-changed');
    currentToken = refreshed;
  }
  // Neither an old completed receipt nor the SDK's cached User flag may substitute for new claims.
  if (currentToken.claims.email_verified !== true) throw new Error('EMAIL_NOT_VERIFIED');
  assertCurrentUser(user);
}

export async function completeLogin(user: AuthUser, invitationToken?: string) {
  const token = await getAuthTokenResult();
  if (!token) throw new Error('login-session-changed');
  assertCurrentUser(user);
  const store = useLoginFinalizeAttemptStore.getState();
  const attempt = store.reserveAttempt(user, token, invitationToken);
  const existing = store.getInFlight(attempt.key);
  if (existing) return existing;
  const work = finalizeAndVerify(user, token, attempt);
  store.setInFlight(attempt.key, work);
  const clear = () => {
    useLoginFinalizeAttemptStore.getState().clearInFlight(attempt.key, work);
  };
  void work.then(clear, clear);
  return work;
}
